/**
 * The moderation workspace: reports to resolve, claims to verify, support messages to answer,
 * duplicates to merge, and the audit log of every decision. Each tab lives in the URL (?tab=).
 */
import { useState } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router'
import { Check, ExternalLink, GitMerge, Mail, RotateCcw, X } from 'lucide-react'
import { Avatar, Empty, Loading, Time } from '../components/Bits'
import { api, ApiError, qs } from '../lib/api'
import { num, recordHref, TYPE_LABELS } from '../lib/format'
import { label, REPORT_REASON_LABELS, SUPPORT_CATEGORY_LABELS } from '../lib/labels'
import { invalidate, useQuery } from '../lib/query'
import { useSession } from '../lib/session'
import { useTitle } from '../lib/useTitle'
import type { Stats } from '../lib/types'
import { ENTITY_TYPES, type EntityType } from '../../shared/model'

interface Report {
  id: string
  target_type: EntityType | 'edit'
  target_id: string
  target_name: string | null
  reason: string
  details: string
  reporter_name: string
  status: string
  resolution: string
  resolver_name: string | null
  created_at: number
}

interface Claim {
  id: string
  entity_type: 'publisher' | 'creator'
  entity_id: string
  entity_name: string
  user_id: string
  user_name: string
  user_avatar: string
  user_email: string | null
  evidence: string
  links: string[]
  status: string
  review_note: string
  created_at: number
}

interface SupportMessage {
  id: string
  category: string
  name: string
  email: string
  user_id: string | null
  user_name: string | null
  subject: string
  message: string
  status: 'open' | 'closed'
  note: string
  closer_name: string | null
  emailed_at: number | null
  created_at: number
  closed_at: number | null
}

const targetHref = (r: { target_type: EntityType | 'edit'; target_id: string }) =>
  r.target_type === 'edit' ? `/edits/${r.target_id}` : recordHref(r.target_type, r.target_id)

function Decision({
  onDecide,
  approveLabel,
  rejectLabel,
  noteRequired = true,
}: {
  onDecide: (approve: boolean, note: string) => Promise<void>
  approveLabel: string
  rejectLabel: string
  noteRequired?: boolean
}) {
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const go = async (approve: boolean) => {
    setBusy(true)
    setErr('')
    try {
      await onDecide(approve, note)
    } catch (x) {
      setErr(x instanceof ApiError ? x.message : 'Could not save')
    } finally {
      // A decision that changes nothing ("Keep") leaves the row in place, still usable.
      setBusy(false)
    }
  }
  return (
    <div className="decision">
      <input
        aria-label="Note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={noteRequired ? 'Required: what was done, for the record…' : 'Note (optional)…'}
        maxLength={2000}
      />
      <button
        type="button"
        className="button button-primary button-small"
        disabled={busy || (noteRequired && !note.trim())}
        onClick={() => go(true)}
      >
        <Check size={14} aria-hidden="true" /> {approveLabel}
      </button>
      <button
        type="button"
        className="button button-quiet button-small"
        disabled={busy || !note.trim()}
        onClick={() => go(false)}
      >
        <X size={14} aria-hidden="true" /> {rejectLabel}
      </button>
      {err && <p className="field-error">{err}</p>}
    </div>
  )
}

function Reports() {
  const [params, setParams] = useSearchParams()
  const status = params.get('status') ?? 'open'
  const { data, refresh } = useQuery<{ items: Report[] }>(`/api/v1/reports${qs({ status })}`, { staleMs: 5000 })
  return (
    <>
      <div className="filters">
        <div className="seg" role="group" aria-label="Status">
          {['open', 'resolved', 'dismissed', 'all'].map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={status === s}
              onClick={() => {
                const next = new URLSearchParams(params)
                next.set('status', s)
                setParams(next, { replace: true })
              }}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>
      {!data ? (
        <Loading />
      ) : data.items.length === 0 ? (
        <Empty title={status === 'open' ? 'No open reports' : 'Nothing here'} />
      ) : (
        <ol className="mod-list">
          {data.items.map((r) => (
            <li key={r.id} className="mod-item">
              <div className="mod-main">
                <p className="mod-title">
                  <span className="status status-reason">{label(REPORT_REASON_LABELS, r.reason)}</span>{' '}
                  <Link to={targetHref(r)}>{r.target_name ?? r.target_id}</Link>{' '}
                  <span className="field-hint">
                    {r.target_type === 'edit' ? 'edit' : TYPE_LABELS[r.target_type].one.toLowerCase()}
                  </span>
                </p>
                {r.details && <p className="mod-text">{r.details}</p>}
                <p className="field-hint">
                  Reported by {r.reporter_name} <Time ms={r.created_at} />
                  {r.status !== 'open' && ` · ${r.status} by ${r.resolver_name}: ${r.resolution}`}
                </p>
              </div>
              {r.status === 'open' && (
                <Decision
                  approveLabel="Resolve"
                  rejectLabel="Dismiss"
                  onDecide={async (ok, note) => {
                    await api(`/api/v1/reports/${r.id}/close`, {
                      method: 'POST',
                      json: { status: ok ? 'resolved' : 'dismissed', note },
                    })
                    invalidate('/api/v1/stats')
                    await refresh()
                  }}
                />
              )}
            </li>
          ))}
        </ol>
      )}
    </>
  )
}

function Claims() {
  const [params, setParams] = useSearchParams()
  const status = params.get('status') ?? 'pending'
  const { data, refresh } = useQuery<{ items: Claim[] }>(`/api/v1/claims${qs({ status })}`, { staleMs: 5000 })
  return (
    <>
      <div className="filters">
        <div className="seg" role="group" aria-label="Status">
          {['pending', 'approved', 'rejected', 'revoked'].map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={status === s}
              onClick={() => {
                const next = new URLSearchParams(params)
                next.set('status', s)
                setParams(next, { replace: true })
              }}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <p className="section-note">
        Check the evidence before approving: contact the publisher through their official site, or confirm the account
        is linked from their official profiles. An approved owner’s edits to their records apply immediately.
      </p>
      {!data ? (
        <Loading />
      ) : data.items.length === 0 ? (
        <Empty title="No claims here" />
      ) : (
        <ol className="mod-list">
          {data.items.map((c) => (
            <li key={c.id} className="mod-item">
              <div className="mod-main">
                <p className="mod-title">
                  <Link to={`/users/${c.user_id}`} className="person">
                    <Avatar name={c.user_name} src={c.user_avatar} size={20} /> {c.user_name}
                  </Link>{' '}
                  claims <Link to={recordHref(c.entity_type, c.entity_id)}>{c.entity_name}</Link>
                </p>
                {c.user_email && <p className="field-hint">Signed-in email: {c.user_email}</p>}
                <p className="mod-text">{c.evidence}</p>
                {c.links.length > 0 && (
                  <ul className="link-list">
                    {c.links.map((l) => (
                      <li key={l}>
                        <a href={l} target="_blank" rel="noopener noreferrer nofollow">
                          <span>{l}</span>
                          <ExternalLink size={14} aria-hidden="true" />
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="field-hint">
                  <Time ms={c.created_at} />
                  {c.review_note && ` · ${c.review_note}`}
                </p>
              </div>
              {c.status === 'pending' && (
                <Decision
                  approveLabel="Approve"
                  rejectLabel="Reject"
                  noteRequired={false}
                  onDecide={async (ok, note) => {
                    await api(`/api/v1/claims/${c.id}/decide`, {
                      method: 'POST',
                      json: { decision: ok ? 'approved' : 'rejected', note },
                    })
                    invalidate('/api/v1/stats', '/api/v1/publishers', '/api/v1/creators')
                    await refresh()
                  }}
                />
              )}
              {c.status === 'approved' && (
                <Decision
                  approveLabel="Keep"
                  rejectLabel="Revoke"
                  noteRequired={false}
                  onDecide={async (ok, note) => {
                    if (ok) return
                    await api(`/api/v1/claims/${c.id}/decide`, { method: 'POST', json: { decision: 'revoked', note } })
                    await refresh()
                  }}
                />
              )}
            </li>
          ))}
        </ol>
      )}
    </>
  )
}

function SupportActions({ m, onDone }: { m: SupportMessage; onDone: () => Promise<void> }) {
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const go = async (action: 'close' | 'reopen') => {
    setBusy(true)
    setErr('')
    try {
      await api(`/api/v1/support/${m.id}/${action}`, { method: 'POST', json: action === 'close' ? { note } : {} })
      // Every status list changes, not only this one.
      invalidate('/api/v1/stats', '/api/v1/support')
      await onDone()
    } catch (x) {
      setErr(x instanceof ApiError ? x.message : 'Could not save')
      setBusy(false)
    }
  }
  const reply = `mailto:${m.email}?subject=${encodeURIComponent(`Re: ${m.subject}`)}`
  return (
    <div className="support-actions">
      <a className="button button-quiet button-small" href={reply}>
        <Mail size={14} aria-hidden="true" /> Reply by email
      </a>
      {m.status === 'open' ? (
        <>
          <input
            aria-label="Note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note (optional): what was done…"
            maxLength={2000}
          />
          <button
            type="button"
            className="button button-primary button-small"
            disabled={busy}
            onClick={() => go('close')}
          >
            <Check size={14} aria-hidden="true" /> Close
          </button>
        </>
      ) : (
        <button type="button" className="button button-quiet button-small" disabled={busy} onClick={() => go('reopen')}>
          <RotateCcw size={14} aria-hidden="true" /> Reopen
        </button>
      )}
      {err && <p className="field-error">{err}</p>}
    </div>
  )
}

function SupportMessages() {
  const [params, setParams] = useSearchParams()
  const status = params.get('status') ?? 'open'
  const { data, refresh } = useQuery<{ items: SupportMessage[] }>(`/api/v1/support${qs({ status })}`, {
    staleMs: 5000,
  })
  return (
    <>
      <div className="filters">
        <div className="seg" role="group" aria-label="Status">
          {['open', 'closed', 'all'].map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={status === s}
              onClick={() => {
                const next = new URLSearchParams(params)
                next.set('status', s)
                setParams(next, { replace: true })
              }}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <p className="section-note">
        Messages from the <Link to="/support">support page</Link>. Reply by email, then close the message; closed
        messages are deleted after a year.
      </p>
      {!data ? (
        <Loading />
      ) : data.items.length === 0 ? (
        <Empty title={status === 'open' ? 'No open messages' : 'Nothing here'} />
      ) : (
        <ol className="mod-list">
          {data.items.map((m) => (
            <li key={m.id} className="mod-item">
              <div className="mod-main">
                <p className="mod-title">
                  <span className="status status-reason">{label(SUPPORT_CATEGORY_LABELS, m.category)}</span>{' '}
                  <strong>{m.subject}</strong>
                </p>
                <p className="field-hint">
                  From {m.name} &lt;<a href={`mailto:${m.email}`}>{m.email}</a>&gt;
                  {m.user_id ? (
                    <>
                      {' · '}
                      <Link to={`/users/${m.user_id}`}>{m.user_name ?? 'their account'}</Link>
                    </>
                  ) : (
                    ' · not signed in'
                  )}{' '}
                  · <Time ms={m.created_at} />
                </p>
                <p className="mod-text">{m.message}</p>
                <p className="field-hint">
                  Reference {m.id}
                  {m.status === 'closed' && ` · closed by ${m.closer_name ?? 'an admin'}${m.note ? `: ${m.note}` : ''}`}
                </p>
              </div>
              <SupportActions m={m} onDone={refresh} />
            </li>
          ))}
        </ol>
      )}
    </>
  )
}

function Duplicates() {
  const [params, setParams] = useSearchParams()
  const type = (params.get('type') as EntityType) ?? 'book'
  const { data, refresh } = useQuery<{
    groups: Array<{ reason: string; key: string; ids: string[]; names: string[] }>
  }>(`/api/v1/duplicates${qs({ type })}`)
  const [busy, setBusy] = useState('')
  const [err, setErr] = useState('')
  const merge = async (from: string, into: string) => {
    setBusy(from)
    setErr('')
    try {
      await api('/api/v1/edits', {
        method: 'POST',
        json: { entity_type: type, entity_id: from, action: 'merge', into, note: 'Duplicate' },
      })
      invalidate('/api/v1/')
      await refresh()
    } catch (x) {
      setErr(x instanceof ApiError ? x.message : 'Could not merge')
    } finally {
      setBusy('')
    }
  }
  return (
    <>
      <div className="filters">
        <label className="filter">
          <span className="filter-label">Record type</span>
          <select
            value={type}
            onChange={(e) => {
              const next = new URLSearchParams(params)
              next.set('type', e.target.value)
              setParams(next, { replace: true })
            }}
          >
            {ENTITY_TYPES.map((t) => (
              <option key={t} value={t}>
                {TYPE_LABELS[t].many}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="section-note">
        Records sharing a name{type === 'book' ? ' or an ISBN' : ''}. Not all are duplicates: two printings or editions
        can share a title. Open both before merging.
      </p>
      {err && (
        <p className="notice notice-danger" role="alert">
          {err}
        </p>
      )}
      {!data ? (
        <Loading />
      ) : data.groups.length === 0 ? (
        <Empty title="No likely duplicates" />
      ) : (
        <ol className="mod-list">
          {data.groups.map((g) => (
            <li key={`${g.reason}:${g.key}`} className="mod-item">
              <div className="mod-main">
                <p className="field-hint">{g.reason}</p>
                <ul className="dupe-group">
                  {g.ids.map((id, i) => (
                    <li key={id}>
                      <Link to={recordHref(type, id)}>{g.names[i]}</Link>
                      {i > 0 && (
                        <button
                          type="button"
                          className="button button-quiet button-small"
                          disabled={busy !== ''}
                          onClick={() => merge(id, g.ids[0]!)}
                        >
                          <GitMerge size={14} aria-hidden="true" /> {busy === id ? 'Merging…' : `Merge into the first`}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </li>
          ))}
        </ol>
      )}
    </>
  )
}

interface AuditRow {
  id: string
  action: string
  target_type: string
  target_id: string
  details: Record<string, unknown>
  created_at: number
  actor_id: string
  actor_name: string | null
}

const AUDIT_TEXT: Record<string, string> = {
  'user.role': 'changed the role of',
  'user.suspend': 'suspended',
  'user.unsuspend': 'reinstated',
  'user.delete': 'deleted the account of',
  'claim.approve': 'verified an owner of',
  'claim.reject': 'rejected a claim on',
  'claim.revoke': 'revoked an owner of',
  'report.resolve': 'resolved a report on',
  'report.dismiss': 'dismissed a report on',
  'record.merge': 'merged',
  'vocab.add': 'added a suggestion to',
  'vocab.remove': 'removed a suggestion from',
  'import.run': 'ran an import from',
}

function Audit() {
  const { data } = useQuery<{ items: AuditRow[] }>('/api/v1/admin/audit')
  if (!data) return <Loading />
  if (!data.items.length)
    return (
      <Empty title="Nothing logged yet">
        Merges, deletions, role changes, claim decisions and closed reports are recorded here, with who made them and
        why.
      </Empty>
    )
  const href = (r: AuditRow) =>
    r.target_type === 'user'
      ? `/users/${r.target_id}`
      : (ENTITY_TYPES as readonly string[]).includes(r.target_type)
        ? recordHref(r.target_type as EntityType, r.target_id)
        : null
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th scope="col">When</th>
            <th scope="col">Who</th>
            <th scope="col">What</th>
            <th scope="col">Details</th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((r) => {
            const to = href(r)
            const details = Object.entries(r.details)
              .filter(([k]) => k !== 'edit' && k !== 'claim' && k !== 'report')
              .map(([k, v]) => `${k}: ${String(v)}`)
              .join(' · ')
            return (
              <tr key={r.id}>
                <td>
                  <Time ms={r.created_at} />
                </td>
                <td>
                  <Link to={`/users/${r.actor_id}`}>{r.actor_name ?? r.actor_id}</Link>
                </td>
                <td>
                  {AUDIT_TEXT[r.action] ?? r.action}{' '}
                  {to ? <Link to={to}>{r.target_type}</Link> : `${r.target_type} ${r.target_id}`}
                </td>
                <td className="wrap">{details}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export function Moderation() {
  useTitle('Moderation')
  const session = useSession()
  const [params, setParams] = useSearchParams()
  const { data: stats } = useQuery<Stats>('/api/v1/stats')
  if (!session.loading && !session.can('moderator')) return <Navigate to="/" replace />
  const admin = session.can('admin')
  const tab = params.get('tab') ?? 'reports'
  const tabs = [
    { id: 'reports', label: 'Reports', count: stats?.open_reports },
    ...(admin
      ? [
          { id: 'claims', label: 'Claims', count: stats?.pending_claims },
          { id: 'support', label: 'Support', count: stats?.open_support },
        ]
      : []),
    { id: 'duplicates', label: 'Duplicates' },
    { id: 'audit', label: 'Audit log' },
  ] as { id: string; label: string; count?: number }[]
  const select = (id: string) => setParams(new URLSearchParams({ tab: id }), { replace: true })
  // Roving focus: arrow keys move between tabs, as role="tab" promises.
  const onKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
    if (!step && e.key !== 'Home' && e.key !== 'End') return
    e.preventDefault()
    const at = tabs.findIndex((t) => t.id === tab)
    const next = e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : (at + step + tabs.length) % tabs.length
    const target = tabs[next]
    if (target) select(target.id)
    e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus()
  }
  return (
    <div className="page">
      <h1 className="page-title">Moderation</h1>
      <p className="lede">
        Flags on bad data, ownership claims, support messages, likely duplicates, and the record of every decision.
      </p>
      <div className="rail mod-tabs" role="tablist" aria-label="Moderation" onKeyDown={onKey}>
        {tabs.map((t) => (
          <button
            key={t.id}
            id={`mod-tab-${t.id}`}
            type="button"
            role="tab"
            className="rail-tab"
            style={{ '--share': 1 / tabs.length } as React.CSSProperties}
            aria-selected={tab === t.id}
            aria-controls="mod-panel"
            tabIndex={tab === t.id ? 0 : -1}
            onClick={() => select(t.id)}
          >
            <span className="rail-label">{t.label}</span>
            {t.count ? <span className="rail-count">{num(t.count)} open</span> : null}
          </button>
        ))}
      </div>
      <div className="tab-panel" role="tabpanel" id="mod-panel" aria-labelledby={`mod-tab-${tab}`}>
        {tab === 'claims' && admin ? (
          <Claims />
        ) : tab === 'support' && admin ? (
          <SupportMessages />
        ) : tab === 'duplicates' ? (
          <Duplicates />
        ) : tab === 'audit' ? (
          <Audit />
        ) : (
          <Reports />
        )}
      </div>
    </div>
  )
}
